import React from 'react';
import { useEffect, useState, useRef } from 'react';
import { Box, Button } from "@mui/material";
import PropTypes from 'prop-types';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { GridToolbarContainer, GridActionsCellItem, } from '@mui/x-data-grid';
import { randomId } from '@mui/x-data-grid-generator';
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { tokens, useHideMode } from "../../theme";
import Header from "../../components/Header";
import { useTheme } from "@mui/material";
import useAxiosPrivate from "../../hooks/useAxiosPrivate"
import ConfirmDialog from '../../components/ConfirmDialog';
import { useNavigate, useLocation } from 'react-router-dom';
import saveTransactions from '../../components/managers/saveTransactions';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import handleError from "../../hooks/handleError";


const Transactions = () => {


    useEffect(() => {
        document.title = 'Transactions | Fretless Finance';
    }, []);

    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const hideTheme = useHideMode();
    const axiosPrivate = useAxiosPrivate();
    const navigate = useNavigate();
    const location = useLocation();
    var [transactions, setTransactions] = useState([]);
    var [displayedTransactions, setDisplayedTransactions] = useState([]);
    var [open, setOpen] = useState(false);
    var [deleteId, setDeleteId] = useState(0);
    const effectRan = useRef(false);
    var isMounted = useRef(false);
    const controller = new AbortController();
    const [pageSize, setPageSize] = useState(50);
    const [nextOffset, setNextOffset] = useState(0);
    const [sortModel, setSortModel] = React.useState([
        {
            field: 'transaction_date',
            sort: 'desc',
        },
    ]);
    var pageNum = 1;


    useEffect(() => {
        isMounted.current = true;
        var success = false;
        if (!effectRan.current) {
            const GetTransactions = async () => {
                try {
                    const response = await axiosPrivate.get(`/transactions?limit=${pageSize}&offset=${nextOffset}&reverseSort=true`, {
                        signal: controller.signal
                    });
                    if (isMounted.current) {
                        const newTransactions = response.data;
                        setTransactions((transactions) => [...transactions, ...newTransactions])
                        setDisplayedTransactions((displayedTransactions) => [...displayedTransactions, ...newTransactions]);

                        var updatedNextOffset = nextOffset + pageSize;
                        setNextOffset(updatedNextOffset);
                        loadMore(updatedNextOffset);
                        success = true
                    }
                } catch (err) {
                    if ((err.response?.status === 401) || (err.response?.status === 403)) {
                        navigate('/sign-in', { state: { from: location }, replace: true });
                    }
                    else if ((err.response?.status === 400) & (err.response?.detail === "Personal Capital login requires Two Factor Authentication")) {
                        // handle pop up for 2FA
                        console.log("Personal Capital requires Two-Factor Authentication")
                    }
                    else {
                        handleError(err);
                    }
                }
            };

            GetTransactions();
            effectRan.current = true;
        }
        return () => {
            if (success) {
                isMounted.current = false;
                controller.abort();
            }
        }
    }, []);



    const loadMore = (offset = nextOffset) => {
        isMounted.current = true;

        const GetTransactions = async () => {
            try {
                const response = await axiosPrivate.get(`/transactions?limit=${pageSize}&offset=${offset}&reverse_sort=true`, {
                    signal: controller.signal
                });
                const newTransactions = response.data;
                setTransactions(transactions => [...transactions, ...newTransactions]);
                setDisplayedTransactions(displayedTransactions => [...displayedTransactions, ...newTransactions]);
                setNextOffset(nextOffset => nextOffset + pageSize);
            } catch (err) {
                handleError(err);
            }
        };

        GetTransactions();
    }

    function handleRenderCell(params, formatValue = false, formatUSD = false) {
        const colName = params.colDef.field;
        const { id, value, api } = params;

        const getCellStyle = () => {
            let color = api.getRow(id).trans_type === "EXPENSE" ? colors.redAccent[400] : colors.greenAccent[400];
            color = api.getRow(id).trans_type === "TRANSFER" ? "#F9A825" : color;
            return { color };
        };

        const getFormattedValue = () => {
            if (!formatValue) {
                return value;
            }
            let amount = value / 100000000;
            amount = (formatUSD) ? amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : amount
            return amount
        };

        return (
            <div style={getCellStyle()}>
                {(hideTheme.mode === "hide" && (colName === "amount" || colName === "crypto_amount")) ? '*'.repeat(9) : getFormattedValue()}
            </div>
        );
    }




    const processRowUpdate = (newRow) => {
        console.log("NEW ROW: ")
        console.log(newRow)
        const updatedRow = { ...newRow, actions: ((newRow.actions !== "INSERT" && newRow.actions !== "DELETE") ? "UPDATE" : newRow.actions) };
        setTransactions(transactions.map((row) => (row.id === updatedRow.id ? updatedRow : row)));
        setDisplayedTransactions(displayedTransactions.map((row) => ((row.id === updatedRow.id & updatedRow.actions !== "DELETE") ? updatedRow : row)));
        return updatedRow;
    };

    const handlePageSizeChange = (newPageSize) => {
        setPageSize(newPageSize)
    }

    const handlePageChange = (page) => {
        pageNum = page + 1;
        const endIndex = pageNum * pageSize;

        if (endIndex == displayedTransactions.length) {
            loadMore();
        }
    }

    const deleteTrans = () => {
        const deletedRow = transactions.filter((row) => row.id === deleteId)[0]
        deletedRow.actions = "DELETE"

        processRowUpdate(deletedRow)
        setDisplayedTransactions(displayedTransactions.filter((row) => row.id !== deleteId));
    };

    const saveChangesHandler = event => {
        var updatedArray = transactions.filter((row) => row.actions !== "DELETE")
        updatedArray = updatedArray.map(row => ({ ...row, actions: null }));

        saveTransactions(transactions);
        setTransactions(updatedArray)
    }

    const TransactionsColumns = [
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 100,
            cellClassName: 'actions',
            getActions: ({ id }) => {
                return [
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label="Delete"
                        onClick={() => {
                            setDeleteId(id);
                            setOpen(true);
                        }}
                        color="inherit"
                    />,
                ];
            },
        },
        {
            field: "transaction_date",
            headerName: "Date",
            headerAlign: 'center',
            width: 100,
            align: 'center',
            cellClassName: "number",
            editable: true,
            valueFormatter: (params) => {
                const date = new Date(params.value * 1000); // convert Unix timestamp to milliseconds
                const formattedDate = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
                return formattedDate;
            }
        },
        {
            field: "amount",
            headerName: "Amount",
            headerAlign: 'center',
            width: 100,
            align: 'right',
            cellClassName: "number",
            editable: true,
            renderCell: (params) => handleRenderCell(params, true, true),
        },
        {
            field: "trans_type",
            headerName: "Type",
            headerAlign: 'center',
            width: 100,
            align: 'center',
            editable: true,
            renderCell: (params) => handleRenderCell(params, false, false),
        },
        {
            field: "source",
            headerName: "Source",
            headerAlign: 'center',
            width: 200,
            editable: true,
            renderCell: (params) => handleRenderCell(params, false, false),
        },
        {
            field: "description",
            headerName: "Description",
            headerAlign: 'center',
            width: 400,
            editable: true,
            renderCell: (params) => handleRenderCell(params, false, false),
        },
        {
            field: "category",
            headerName: "Category",
            headerAlign: 'center',
            width: 150,
            editable: true,
            renderCell: (params) => handleRenderCell(params, false, false),
        },
        {
            field: "subcategory",
            headerName: "Subcategory",
            headerAlign: 'center',
            width: 150,
            editable: true,
            renderCell: (params) => handleRenderCell(params, false, false),
        },

        {
            field: "app_account",
            headerName: "Account",
            headerAlign: 'center',
            width: 100,
            align: 'center',
            editable: true,
            renderCell: (params) => handleRenderCell(params, false, false),
        },

        {
            field: "money_account",
            headerName: "Money Account",
            headerAlign: 'center',
            align: 'left',
            width: 350,
            align: 'left',
            editable: true,
            renderCell: (params) => handleRenderCell(params, false, false),
        },

        {
            field: "bucket",
            headerName: "Bucket",
            headerAlign: 'center',
            align: 'left',
            width: 250,
            editable: true,
            renderCell: (params) => handleRenderCell(params, false, false),
        },

        {
            field: "sub_bucket",
            headerName: "SubBucket",
            headerAlign: 'center',
            align: 'left',
            width: 250,
            editable: true,
            renderCell: (params) => handleRenderCell(params, false, false),
        },

        {
            field: "hsa_expense",
            headerName: "HSA Expense",
            headerAlign: 'center',
            width: 150,
            align: 'center',
            editable: true,
            renderCell: (params) => handleRenderCell(params, false, false),
        },

        {
            field: "income_type",
            headerName: "Income Type",
            headerAlign: 'center',
            width: 150,
            editable: true,
            renderCell: (params) => handleRenderCell(params, false, false),
        },

        {
            field: "income_source",
            headerName: "Income Source",
            headerAlign: 'center',
            width: 150,
            editable: true,
            renderCell: (params) => handleRenderCell(params, false, false),
        },

        {
            field: "is_subscription",
            headerName: "Is Subscription",
            headerAlign: 'center',
            width: 150,
            align: 'center',
            editable: true,
            renderCell: (params) => handleRenderCell(params, false, false),
        },

        {
            field: "crypto_amount",
            headerName: "Crypto Amount",
            headerAlign: 'center',
            width: 150,
            align: 'right',
            editable: true,
            renderCell: (params) => handleRenderCell(params, true, false),
        },

        {
            field: "currency",
            headerName: "Currency",
            headerAlign: 'center',
            align: 'center',
            width: 200,
            editable: true,
            renderCell: (params) => handleRenderCell(params, false, false),
        },
        {
            field: "receipt_saved",
            headerName: "Receipt Saved",
            headerAlign: 'center',
            width: 200,
            align: 'center',
            editable: true,
            renderCell: (params) => handleRenderCell(params, false, false),
        },

        {
            field: "receipt_location",
            headerName: "Receipt Location",
            headerAlign: 'center',
            width: 300,
            align: 'left',
            editable: true,
            renderCell: (params) => handleRenderCell(params, false, false),
        },

        {
            field: "included_items",
            headerName: "Included Items",
            headerAlign: 'center',
            align: 'left',
            width: 400,
            editable: true,
            renderCell: (params) => handleRenderCell(params, false, false),
        },

        {
            field: "invoice_id",
            headerName: "Invoice ID",
            headerAlign: 'center',
            align: 'left',
            width: 200,
            editable: true,
            renderCell: (params) => handleRenderCell(params, false, false),
        },

        {
            field: "transaction_source",
            headerName: "Transaction Source",
            headerAlign: 'center',
            width: 150,
            align: 'left',
            editable: true,
            renderCell: (params) => handleRenderCell(params, false, false),
        },

        {
            field: "notes",
            headerName: "Notes",
            headerAlign: 'center',
            width: 500,
            align: 'left',
            editable: true,
            renderCell: (params) => handleRenderCell(params, false, false),
        },
        {
            field: "needs_reviewed",
            headerName: "Needs Reviewed",
            headerAlign: 'center',
            align: 'left',
            width: 200,
            editable: true,
            renderCell: (params) => handleRenderCell(params, false, false),
        },
    ]


    return (
        <Box m="20px">
            <Header
                title="TRANSACTIONS"
                titleSize="h2"
                subtitle="ALL of your transactions. Like too many."
                subtitleSize="h5"
            />
            < ToastContainer />

            <Box
                m="40px 0 0 0"
                height="67vh"
                sx={{
                    "& .MuiDataGrid-root": {
                        border: "none",
                    },
                    "& .MuiDataGrid-cell": {
                        borderBottom: "none",
                    },
                    "& .name-column--cell": {
                        color: colors.greenAccent[300],
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: colors.blueAccent[400],
                        borderBottom: "none",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                        backgroundColor: colors.primary[400],
                    },
                    "& .MuiDataGrid-footerContainer": {
                        borderTop: "none",
                        backgroundColor: colors.blueAccent[400],
                    },
                    "& .MuiCheckbox-root": {
                        color: `${colors.greenAccent[200]} !important`,
                    },
                    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                        color: `${colors.grey[100]} !important`,

                    },
                }}
            >
                <Box
                    m="20px 0 0 0"
                    height="100%"
                    sx={{
                        "& .MuiDataGrid-root": {
                            border: "none",
                        },
                        "& .MuiDataGrid-cell": {
                            borderBottom: "none",
                        },
                        "& .name-column--cell": {
                            color: colors.greenAccent[300],
                        },
                        "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: colors.blueAccent[400],
                            borderBottom: "none",
                        },
                        "& .MuiDataGrid-virtualScroller": {
                            backgroundColor: colors.primary[400],
                        },
                        "& .MuiDataGrid-footerContainer": {
                            borderTop: "none",
                            backgroundColor: colors.blueAccent[400],
                        },
                        "& .MuiCheckbox-root": {
                            color: `${colors.greenAccent[200]} !important`,
                        },
                        "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                            color: `${colors.grey[100]} !important`,
                        },
                    }}
                >

                    < DataGrid

                        rows={displayedTransactions}
                        columns={TransactionsColumns}
                        sortModel={sortModel}
                        onSortModelChange={(model) => setSortModel(model)}
                        pagination
                        pageSize={pageSize}
                        onPageSizeChange={(newPageSize) => handlePageSizeChange(newPageSize)}
                        rowsPerPageOptions={[50, 100, 200]}
                        // paginationMode="server"
                        onPageChange={(newPage) => handlePageChange(newPage)}
                        processRowUpdate={processRowUpdate}
                        experimentalFeatures={{ newEditingApi: true }}
                        components={{ Toolbar: GridToolbar }
                        }
                        sx={
                            {
                                fontSize: 15,
                                '& .MuiDataGrid-cell:edit': {
                                    fontSize: 10,
                                },
                                CanUserResizeColumns: true,
                            }

                        }
                    // checkboxSelection
                    //onRowClick={handleRowClick}
                    />
                </Box>
                <ConfirmDialog
                    title="Delete Transaction"
                    message={"Are you sure you want to delete this? It may come back after next sync."}
                    confirmBtnText="DELETE"
                    open={open}
                    setOpen={setOpen}
                    onConfirm={deleteTrans}>
                </ConfirmDialog>
            </Box>
            <Box padding="1rem" align="right">
                <Button onClick={saveChangesHandler}
                    sx={{
                        backgroundColor: colors.greenAccent[500],
                        color: "#ffffff",
                        '&:hover': {
                            backgroundColor: colors.greenAccent[300],
                            color: "#ffffff",
                        },
                        fontSize: "14px",
                        fontWeight: "bold",
                        padding: "10px 20px",
                    }}
                >
                    <SaveIcon sx={{ mr: "10px" }} />
                    Save Changes
                </Button>
            </Box>

        </Box >
    );
};

export default Transactions;